/**
 * HomeV3 — page d'accueil publique (route `/`).
 *
 * Transposition iso-pixel de l'export Claude Design du 23/09/2026,
 * `design-refs/home-v3/` (bundle.html = la référence, template.html = le
 * DOM, page-source.jsx = la logique). Le corps ci-dessous EST le DOM de
 * template.html : mêmes balises, mêmes classes, mêmes styles inline, même
 * ordre. Il a été produit par conversion mécanique, pas retapé ; les
 * textes vivent dans `i18n/homeV3.json`, le français y est copié de la
 * référence au caractère près.
 *
 * Écarts de forme imposés par React, sans effet sur le rendu :
 *   - `<image-slot>` → `HV3Slot` : un cadre `overflow:hidden` arrondi selon
 *     la forme (cercle 50 %, arrondi 12 px, rectangle), image en `cover`
 *     centrée. C'est la géométrie que le composant image-slot du bundle
 *     calcule en JS. Les `object-position` 24 % / 22 % de la feuille du
 *     design visent l'<img> interne, dont la boîte a déjà le ratio de
 *     l'image : ils n'ont aucun effet dans la référence, donc pas ici non
 *     plus.
 *   - `{{ placeholders }}` → état React porté de page-source.jsx : étape
 *     (0 par défaut, clic sur les cartes 01 à 04) et frise `tl` (1 par
 *     défaut, +1 toutes les 2 s, figée sous prefers-reduced-motion).
 *
 * Le header et le footer sont ceux du site (`PublicHeader`,
 * `SiteFooterV2`) : leur DOM est celui de la référence. Le header est
 * monté en mode `authCta="dashboard"` : un seul bouton « Mon Dashboard »,
 * connecté ou non, comme sur la référence.
 *
 * L'ancienne home (`HomeV2`) reste dans le dépôt, non routée.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteHeaderV2 from '../components/siteV2/SiteHeaderV2';
import SiteFooterV2 from '../components/siteV2/SiteFooterV2';
import VideoModal from '../components/siteV2/VideoModal';
import useStartRoute from '../hooks/useStartRoute';
import useFeatureFlags from '../hooks/useFeatureFlags';
import { heroVideoFor } from '../config/heroVideo';
import PageHead from '../components/seo/PageHead';
import GoogleAnalytics from '../components/seo/GoogleAnalytics';
import { useSiteLanguage } from '../hooks/useSiteLanguage';
import { createT } from '../i18n/createT';
import { getPageMetadata } from '../lib/seo/metadata';
import dict from '../i18n/homeV3.json';
import '../styles/home-v3.css';

/** `a:b;c:d` → `{ a: 'b', c: 'd' }` — les styles de carte de page-source.jsx sont des chaînes. */
function cssToStyle(css) {
  const out = {};
  css.split(';').forEach((decl) => {
    const i = decl.indexOf(':');
    if (i < 0) return;
    const prop = decl.slice(0, i).trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    out[prop] = decl.slice(i + 1).trim();
  });
  return out;
}

/** page-source.jsx › cardStyle(i), à l'identique. */
function cardStyleFor(i, step) {
  const on = step === i;
  const accent = i === 3 ? '#16a34a' : '#1a56db';
  return cssToStyle('background:#fff;border:1px solid ' + (on ? accent : '#ECECEA') +
    ';border-radius:14px;padding:22px 20px;cursor:pointer;transition:all .18s ease;text-align:left;font:inherit;box-shadow:' +
    (on ? '0 12px 32px rgba(26,86,219,.14)' : '0 2px 12px rgba(0,0,0,.03)') +
    ';transform:' + (on ? 'translateY(-4px)' : 'none'));
}

/**
 * `<image-slot>` de la référence, à la géométrie près.
 *
 * Le composant du bundle ne pose pas un `object-fit` : il place l'image en
 * absolu, centrée (`left/top: 50%`, `translate(-50%,-50%)`), et calcule en
 * JS sa largeur et sa hauteur en % du cadre pour la couvrir
 * (`base = max(fw/iw, fh/ih)`, avec fw/fh = clientWidth/clientHeight de
 * l'hôte). Un `object-fit: cover` donne le même cadrage mais ne
 * rééchantillonne pas au même sous-pixel : le diff le voit. On refait donc
 * le même calcul, au chargement de l'image et à chaque redimensionnement.
 * Avant la mesure (et dans le HTML pré-rendu), le repli est celui du
 * composant : 100 % × 100 % en `cover`.
 *
 * L'hôte garde `position: relative` du `:host` du composant, mais PAS son
 * `aspect-ratio: 3/2` : là où l'hôte a une hauteur en %, ce ratio élargit
 * la colonne de la section mission à 450 px à 390 et coupe le texte à
 * droite. Défaut de la référence, corrigé sur décision de Romain
 * (23/09/2026) ; sans effet à 1440.
 *
 * `imgStyle` porte les règles `::part(image)` de la référence
 * (`object-fit` / `object-position` des portraits de témoins).
 */
function HV3Slot({ src, hostStyle, frameStyle, imgStyle, ...rest }) {
  const hostRef = useRef(null);
  const imgRef = useRef(null);
  useEffect(() => {
    const host = hostRef.current;
    const img = imgRef.current;
    if (!host || !img) return undefined;
    const apply = () => {
      const iw = img.naturalWidth; const ih = img.naturalHeight;
      const fw = host.clientWidth; const fh = host.clientHeight;
      if (!iw || !ih || !fw || !fh) return;
      const base = Math.max(fw / iw, fh / ih);
      img.style.width = (iw * base / fw * 100) + '%';
      img.style.height = (ih * base / fh * 100) + '%';
      img.style.objectFit = (imgStyle && imgStyle.objectFit) || '';
    };
    img.addEventListener('load', apply);
    if (img.complete) apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (ro) ro.observe(host);
    return () => { img.removeEventListener('load', apply); if (ro) ro.disconnect(); };
  }, [src, imgStyle]);
  return (
    <span ref={hostRef} className="hv3-slot" {...rest} style={{ position: 'relative', ...hostStyle }}>
      <span
        className="hv3-slot-frame"
        style={{ display: 'block', position: 'absolute', inset: 0, overflow: 'hidden', background: 'rgba(127,127,127,.08)', ...frameStyle }}
      >
        <img
          ref={imgRef}
          className="hv3-slot-img"
          src={src}
          alt=""
          draggable={false}
          style={{ position: 'absolute', display: 'block', maxWidth: 'none', transform: 'translate(-50%,-50%)', width: '100%', height: '100%', left: '50%', top: '50%', objectFit: 'cover', ...imgStyle }}
        />
      </span>
    </span>
  );
}

// image-slot[data-portrait]::part(image) et #ph-temoin-4::part(image)
const IMG_PORTRAIT_X = { objectFit: 'cover', objectPosition: '50% 24%' };
const IMG_PORTRAIT_4 = { objectFit: 'cover', objectPosition: '58% 22%' };

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function HomeV3() {
  const navigate = useNavigate();
  const [language] = useSiteLanguage();
  const t = useMemo(() => createT(dict, language), [language]);
  const flags = useFeatureFlags();
  // Allumé par défaut, y compris avant la réponse de /api/public-config :
  // le drapeau ne sert qu'à éteindre la section d'un geste.
  const testimonialsOn = flags?.home_testimonials_enabled !== false;

  const startRoute = useStartRoute();
  const goStart = useCallback(() => navigate(startRoute), [navigate, startRoute]);

  // page-source.jsx › state = { step: 0, tl: 1 }
  const [step, setStep] = useState(0);
  const [tl, setTl] = useState(1);
  useEffect(() => {
    // Le pré-rendu fige la frise sur son état initial : sinon le HTML servi
    // porte un autre `tl` que le premier rendu client.
    if (prefersReducedMotion() || window.__ARCHER_PRERENDER__) return undefined;
    const id = setInterval(() => setTl((s) => (s + 1) % 3), 2000);
    return () => clearInterval(id);
  }, []);
  const tl0 = tl === 0 ? '1' : '0';
  const tl1 = tl === 1 ? '1' : '0';
  const tl2 = tl === 2 ? '1' : '0';
  const isStep0 = step === 0;
  const isStep1 = step === 1;
  const isStep2 = step === 2;
  const isStep3 = step === 3;
  const cardStyle = (i) => cardStyleFor(i, step);
  const st = t(`steps.${step}`);

  // « Lire notre lettre » et « Voir la vidéo » : inertes tant que la page
  // de la lettre n'existe pas (décision du 23/09/2026 : aucun renvoi de
  // remplacement, ni vers /attorneys ni vers la vidéo du hero).
  const inert = (e) => e.preventDefault();

  // page-source.jsx › scrollTesti(dir, ev)
  const scrollTesti = (dir, ev) => {
    const btn = ev && ev.currentTarget;
    const scope = btn && btn.closest('section');
    const el = (scope || document).querySelector('.hv2-testi-rail');
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8) });
  };

  // « Voir comment ça marche » : la vidéo de la langue si elle existe,
  // sinon l'ancre #journey (même logique que la home précédente).
  const heroVideo = heroVideoFor(language);
  const [videoOpen, setVideoOpen] = useState(false);
  useEffect(() => { setVideoOpen(false); }, [language]);
  const openVideo = (e) => {
    e.preventDefault();
    e.currentTarget.focus();
    setVideoOpen(true);
  };
  const onVideoUnavailable = useCallback(() => {
    setVideoOpen(false);
    document.getElementById('journey')?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, []);

  const seoMeta = getPageMetadata('home', language);

  return (
    <div className="hv2-root" data-testid="home-page" lang={language}>
      <PageHead metadata={seoMeta} />
      <GoogleAnalytics />
      <SiteHeaderV2 authCta="dashboard" />

        {/* ═══ hero ═══ */}
        <section className="archer-hero-section" style={{ "position": "relative", "paddingLeft": "40px", "paddingRight": "40px", "overflow": "hidden", "background": "rgb(255, 255, 255)" }}>
          <div aria-hidden="true" className="archer-hero-halo archer-hero-halo--wide" />
          <div className="hv2-hero-row" style={{ "maxWidth": "1160px", "margin": "0px auto", "position": "relative", "display": "flex", "alignItems": "center", "gap": "64px", "flexWrap": "wrap" }}>
            <div style={{ "flex": "1 1 540px", "minWidth": "0px" }}>
              <div style={{ "display": "inline-flex", "alignItems": "center", "gap": "7px", "fontSize": "12.5px", "fontWeight": "500", "letterSpacing": "-0.005em", "color": "rgb(75, 85, 99)", "background": "rgb(255, 255, 255)", "border": "1px solid rgb(229, 231, 235)", "padding": "5px 12px 5px 6px", "borderRadius": "999px", "marginBottom": "26px" }}>
                <span style={{ "display": "inline-flex", "alignItems": "center", "gap": "6px", "background": "rgb(240, 253, 244)", "color": "rgb(21, 128, 61)", "fontSize": "11px", "fontWeight": "600", "padding": "2px 8px", "borderRadius": "999px", "animation": "1.6s ease-in-out 0s infinite normal none running hv2NewFlash" }}>
                  <span style={{ "width": "6px", "height": "6px", "borderRadius": "50%", "background": "rgb(22, 163, 74)", "animation": "1.6s ease 0s infinite normal none running hv2LiveDot" }} />
                  {t('hero.t01')}
                </span>
                {t('hero.t02')}
              </div>
              <h1 className="archer-hero-title">
                {t('hero.t03')}
                <br />
                <span className="archer-hero-accent">
                  {t('hero.t04')}
                </span>
              </h1>
              <p className="archer-hero-subtitle" style={{ "maxWidth": "520px" }}>
                {t('hero.t05')}
              </p>
              <p className="archer-hero-subtitle" style={{ "maxWidth": "520px", "marginTop": "8px" }}>
                {t('hero.t06')}
                <strong style={{ "fontWeight": "600" }}>
                  {t('hero.t07')}
                </strong>
                {t('hero.t08')}
              </p>
              <div style={{ "display": "flex", "gap": "10px", "flexWrap": "wrap", "marginTop": "28px" }}>
                <button onClick={goStart} type="button" className="hv2-btn-primary-lift" style={{ "display": "inline-flex", "alignItems": "center", "gap": "8px", "padding": "11px 20px", "borderWidth": "medium", "borderStyle": "none", "borderColor": "currentcolor", "borderImage": "none", "background": "rgb(26, 86, 219)", "color": "rgb(255, 255, 255)", "borderRadius": "10px", "fontSize": "15px", "fontWeight": "500", "letterSpacing": "-0.01em", "fontFamily": "inherit", "cursor": "pointer", "transition": "0.15s" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  {t('hero.t09')}
                </button>
                <a aria-haspopup={heroVideo ? 'dialog' : undefined} onClick={heroVideo ? openVideo : undefined} className="hv2-btn-ghost" href="#journey" data-testid="hero-how-it-works" style={{ "display": "inline-flex", "alignItems": "center", "gap": "7px", "padding": "11px 18px", "background": "rgb(255, 255, 255)", "border": "1px solid rgb(229, 231, 235)", "color": "rgb(10, 10, 15)", "borderRadius": "10px", "fontSize": "15px", "fontWeight": "500", "letterSpacing": "-0.01em", "cursor": "pointer", "transition": "0.15s" }}>
                  <svg width="17" height="17" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" fill="none" stroke="#1a56db" strokeWidth="1.8" />
                    <polygon points="10 8.5 16 12 10 15.5" fill="#1a56db" stroke="none" />
                  </svg>
                  {t('hero.t10')}
                </a>
              </div>
              <div className="hv2-avatars" style={{ "display": "flex", "alignItems": "center", "gap": "14px", "flexWrap": "wrap", "marginTop": "24px" }}>
                {"\n"}
                <div style={{ "display": "flex", "alignItems": "center", "paddingLeft": "11px" }}>
                  <span style={{ "flex": "0 0 auto", "width": "36px", "height": "36px", "borderRadius": "50%", "overflow": "hidden", "background": "#F4F4F1", "boxShadow": "0 0 0 2.5px #ffffff, 0 2px 8px rgba(10,10,15,0.10)", "marginLeft": "-11px" }}>
                    <HV3Slot id="ph-team-1" data-mini="" src="/home-v3/equipe-avatar.webp" hostStyle={{ "display": "block", "width": "36px", "height": "36px" }} frameStyle={{borderRadius: "50%"}} />
                  </span>
                  <span style={{ "flex": "0 0 auto", "width": "36px", "height": "36px", "borderRadius": "50%", "overflow": "hidden", "background": "#F4F4F1", "boxShadow": "0 0 0 2.5px #ffffff, 0 2px 8px rgba(10,10,15,0.10)", "marginLeft": "-11px" }}>
                    <HV3Slot id="ph-team-2" data-mini="" src="/home-v3/camille-avatar.webp" hostStyle={{ "display": "block", "width": "36px", "height": "36px" }} frameStyle={{borderRadius: "50%"}} />
                  </span>
                  <span style={{ "flex": "0 0 auto", "width": "36px", "height": "36px", "borderRadius": "50%", "overflow": "hidden", "background": "#F4F4F1", "boxShadow": "0 0 0 2.5px #ffffff, 0 2px 8px rgba(10,10,15,0.10)", "marginLeft": "-11px" }}>
                    <HV3Slot id="ph-team-3" data-mini="" src="/home-v3/equipe-avatar.webp" hostStyle={{ "display": "block", "width": "36px", "height": "36px" }} frameStyle={{borderRadius: "50%"}} />
                  </span>
                </div>
                {"\n"}
                <p style={{ "flex": "1 1 300px", "minWidth": "260px", "margin": "0", "fontSize": "13px", "fontWeight": "400", "lineHeight": "1.55", "color": "#6B7280" }}>
                  {t('hero.t14')}
                  <a href="#equipe" style={{ "color": "#1A56DB", "fontWeight": "600", "whiteSpace": "nowrap" }}>
                    {t('hero.t15')}
                  </a>
                </p>
                {"\n"}
              </div>
              <div style={{ "display": "flex", "gap": "10px 16px", "flexWrap": "wrap", "marginTop": "30px", "fontSize": "12.5px", "fontWeight": "400", "color": "rgb(107, 114, 128)" }}>
                <span style={{ "display": "inline-flex", "alignItems": "center", "gap": "7px", "whiteSpace": "nowrap" }}>
                  <span style={{ "flex": "0 0 auto", "width": "22px", "height": "22px", "borderRadius": "7px", "background": "rgb(254, 243, 199)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                  <span style={{ "color": "rgb(10, 10, 15)", "fontWeight": "600" }}>
                    {t('hero.t16')}
                  </span>
                  {t('hero.t17')}
                </span>
                <span style={{ "display": "inline-flex", "alignItems": "center", "gap": "7px", "whiteSpace": "nowrap" }}>
                  <span style={{ "flex": "0 0 auto", "width": "22px", "height": "22px", "borderRadius": "7px", "background": "rgb(239, 246, 255)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" stroke="#1a56db" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                  </span>
                  <span style={{ "color": "rgb(10, 10, 15)", "fontWeight": "600" }}>
                    {t('hero.t18')}
                  </span>
                </span>
                <span style={{ "display": "inline-flex", "alignItems": "center", "gap": "7px", "whiteSpace": "nowrap" }}>
                  <span style={{ "flex": "0 0 auto", "width": "22px", "height": "22px", "borderRadius": "7px", "background": "rgb(220, 252, 231)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <polyline points="12 7 12 12 15 14" />
                    </svg>
                  </span>
                  <span style={{ "color": "rgb(10, 10, 15)", "fontWeight": "600" }}>
                    {t('hero.t19')}
                  </span>
                  {t('hero.t20')}
                </span>
              </div>
            </div>
            <div className="hv2-hero-duo" style={{ "flex": "1 1 400px", "minWidth": "320px", "display": "flex", "flexDirection": "column", "alignItems": "center", "gap": "16px" }}>
              {"\n"}
              <div style={{ "position": "relative", "width": "100%", "maxWidth": "380px", "display": "flex", "justifyContent": "center" }}>
                {"\n"}
                <div style={{ "position": "absolute", "left": "50%", "top": "48%", "transform": "translate(-50%, -50%)", "width": "320px", "height": "320px", "borderRadius": "50%", "background": "radial-gradient(circle at center, rgba(26,86,219,0.085) 0%, rgba(26,86,219,0) 68%)", "pointerEvents": "none" }} />
                {"\n"}
                <picture style={{ "display": "block", "position": "relative" }}>
                  <img alt="" aria-hidden="true" src="/home-v3/hero-owl.png" style={{ "width": "100%", "maxWidth": "380px", "height": "360px", "objectFit": "contain", "display": "block" }} />
                </picture>
                {"\n"}
              </div>
              {"\n"}
              <div style={{ "display": "flex", "alignItems": "center", "gap": "11px", "background": "#ffffff", "border": "1px solid #ECECEA", "borderRadius": "999px", "boxShadow": "0 8px 24px rgba(10,10,15,0.07)", "padding": "7px 18px 7px 7px" }}>
                {"\n"}
                <span style={{ "flex": "0 0 auto", "width": "36px", "height": "36px", "borderRadius": "50%", "overflow": "hidden", "background": "#F4F4F1" }}>
                  <HV3Slot id="ph-camille-hero" data-mini="" src="/home-v3/camille-avatar.webp" hostStyle={{ "display": "block", "width": "36px", "height": "36px" }} frameStyle={{borderRadius: "50%"}} />
                </span>
                {"\n"}
                <span style={{ "fontSize": "13px", "fontWeight": "600", "letterSpacing": "-0.01em", "color": "#0a0a0f" }}>
                  {t('hero.t22')}
                </span>
                {"\n"}
              </div>
              {"\n"}
            </div>
          </div>
        </section>
        {/* ═══ trio ═══ */}
        <section style={{ "background": "rgb(255, 255, 255)", "borderTop": "1px solid rgb(232, 232, 228)", "padding": "76px 40px 72px" }}>
          <div style={{ "maxWidth": "1000px", "margin": "0px auto" }}>
            {"\n"}
            <div style={{ "maxWidth": "620px", "marginBottom": "34px" }}>
              {"\n"}
              <div style={{ "fontSize": "11.5px", "fontWeight": "600", "letterSpacing": "0.08em", "textTransform": "uppercase", "color": "rgb(26, 86, 219)", "marginBottom": "8px" }}>
                {t('trio.t01')}
              </div>
              {"\n"}
              <div style={{ "fontSize": "clamp(24px, 2.8vw, 32px)", "fontWeight": "600", "letterSpacing": "-0.028em", "lineHeight": "1.15", "color": "rgb(10, 10, 15)" }}>
                {t('trio.t02')}
                <span style={{ "color": "#1A56DB" }}>
                  {t('trio.t03')}
                </span>
              </div>
              {"\n"}
              <p style={{ "margin": "14px 0 0", "fontSize": "15.5px", "fontWeight": "400", "lineHeight": "1.6", "color": "#4B5563", "maxWidth": "560px" }}>
                {t('trio.t04')}
              </p>
              {"\n"}
            </div>
            {"\n"}
            <div style={{ "marginTop": "34px", "display": "flex", "flexDirection": "column", "gap": "14px" }}>
              {"\n"}
              <div className="hv2-timeline-row" style={{ "display": "grid", "gridTemplateColumns": "132px 34px 1fr", "alignItems": "stretch" }}>
                {"\n"}
                <div style={{ "padding": "22px 18px 0 0", "display": "flex", "flexDirection": "column", "alignItems": "flex-end", "gap": "5px" }}>
                  <div className="hv2-tlpill" data-active={tl0} style={{ "display": "inline-flex", "alignItems": "center", "gap": "7px", "background": "#F4F4F1", "border": "1px solid #E5E7EB", "borderRadius": "999px", "padding": "5px 11px 5px 9px" }}>
                    <span className="hv2-tlpin" data-active={tl0} aria-hidden="true" style={{ "width": "5px", "height": "5px", "borderRadius": "50%", "background": "#C3C7CE" }} />
                    <span className="hv2-tlbig" data-active={tl0} style={{ "fontSize": "12.5px", "fontWeight": "700", "letterSpacing": "-0.015em", "whiteSpace": "nowrap", "color": "#6B7280" }}>
                      {t('trio.t05')}
                    </span>
                  </div>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "lineHeight": "1.35", "textAlign": "right", "color": "#A8ACB4" }}>
                    {t('trio.t06')}
                  </div>
                </div>
                {"\n"}
                <div style={{ "position": "relative" }}>
                  <div className="hv2-tlseg" data-active={tl0} aria-hidden="true" style={{ "position": "absolute", "left": "11px", "top": "30px", "bottom": "-12px", "width": "2px", "background": "#E5E7EB" }} />
                  <span className="hv2-tldot" data-active={tl0} aria-hidden="true" style={{ "position": "absolute", "left": "0", "top": "20px", "width": "24px", "height": "24px", "borderRadius": "50%", "background": "#ffffff", "border": "2px solid #E5E7EB" }} />
                </div>
                {"\n"}
                <div className="hv2-tlcard" data-active={tl0} style={{ "background": "#ffffff", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "24px 28px", "display": "flex", "alignItems": "center", "gap": "18px", "boxShadow": "0 2px 12px rgba(0,0,0,0.03)" }}>
                  {"\n"}
                  <span style={{ "flex": "0 0 auto", "width": "56px", "height": "56px", "borderRadius": "15px", "background": "#EFF6FF", "display": "flex", "alignItems": "center", "justifyContent": "center", "overflow": "hidden" }}>
                    <img alt="" aria-hidden="true" src="/home-v3/hero-owl.png" style={{ "width": "44px", "height": "44px", "objectFit": "contain", "display": "block" }} />
                  </span>
                  {"\n"}
                  <div style={{ "flex": "1 1 0%", "minWidth": "0" }}>
                    {"\n"}
                    <div style={{ "fontSize": "10.5px", "fontWeight": "700", "letterSpacing": "0.08em", "textTransform": "uppercase", "color": "#9CA3AF" }}>
                      {t('trio.t08')}
                    </div>
                    {"\n"}
                    <div style={{ "marginTop": "6px", "fontSize": "18px", "fontWeight": "700", "letterSpacing": "-0.024em" }}>
                      {t('trio.t09')}
                    </div>
                    {"\n"}
                    <div style={{ "marginTop": "6px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "#4B5563" }}>
                      {t('trio.t10')}
                    </div>
                    {"\n"}
                  </div>
                  {"\n"}
                  <span style={{ "flex": "0 0 auto", "fontSize": "14.5px", "fontWeight": "700", "color": "#16a34a" }}>
                    {t('trio.t11')}
                  </span>
                  {"\n"}
                </div>
                {"\n"}
              </div>
              {"\n"}
              <div className="hv2-timeline-row" style={{ "display": "grid", "gridTemplateColumns": "132px 34px 1fr", "alignItems": "stretch" }}>
                {"\n"}
                <div style={{ "padding": "22px 18px 0 0", "display": "flex", "flexDirection": "column", "alignItems": "flex-end", "gap": "5px" }}>
                  <div className="hv2-tlpill" data-active={tl1} style={{ "display": "inline-flex", "alignItems": "center", "gap": "7px", "background": "#F4F4F1", "border": "1px solid #E5E7EB", "borderRadius": "999px", "padding": "5px 11px 5px 9px" }}>
                    <span className="hv2-tlpin" data-active={tl1} aria-hidden="true" style={{ "width": "5px", "height": "5px", "borderRadius": "50%", "background": "#C3C7CE" }} />
                    <span className="hv2-tlbig" data-active={tl1} style={{ "fontSize": "12.5px", "fontWeight": "700", "letterSpacing": "-0.015em", "whiteSpace": "nowrap", "color": "#6B7280" }}>
                      {t('trio.t12')}
                    </span>
                  </div>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "lineHeight": "1.35", "textAlign": "right", "color": "#A8ACB4" }}>
                    {t('trio.t13')}
                  </div>
                </div>
                {"\n"}
                <div style={{ "position": "relative" }}>
                  <div className="hv2-tlseg" data-active={tl1} aria-hidden="true" style={{ "position": "absolute", "left": "11px", "top": "-12px", "bottom": "-12px", "width": "2px", "background": "#E5E7EB" }} />
                  <span className="hv2-tldot" data-active={tl1} aria-hidden="true" style={{ "position": "absolute", "left": "0", "top": "20px", "width": "24px", "height": "24px", "borderRadius": "50%", "background": "#ffffff", "border": "2px solid #E5E7EB" }} />
                </div>
                {"\n"}
                <div className="hv2-tlcard" data-active={tl1} style={{ "background": "#ffffff", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "24px 28px", "display": "flex", "alignItems": "center", "gap": "18px", "boxShadow": "0 2px 12px rgba(0,0,0,0.03)" }}>
                  {"\n"}
                  <span style={{ "flex": "0 0 auto", "width": "56px", "height": "56px", "borderRadius": "15px", "overflow": "hidden", "background": "#F4F4F1" }}>
                    <HV3Slot id="ph-camille-trio" src="/home-v3/camille-trio.webp" hostStyle={{ "display": "block", "width": "56px", "height": "56px" }} frameStyle={{borderRadius: "12px"}} />
                  </span>
                  {"\n"}
                  <div style={{ "flex": "1 1 0%", "minWidth": "0" }}>
                    {"\n"}
                    <div style={{ "fontSize": "10.5px", "fontWeight": "700", "letterSpacing": "0.08em", "textTransform": "uppercase", "color": "#1A56DB" }}>
                      {t('trio.t15')}
                    </div>
                    {"\n"}
                    <div style={{ "marginTop": "6px", "fontSize": "18px", "fontWeight": "700", "letterSpacing": "-0.024em" }}>
                      {t('trio.t16')}
                      <span style={{ "color": "#1A56DB" }}>
                        {t('trio.t17')}
                      </span>
                      {t('trio.t18')}
                    </div>
                    {"\n"}
                    <div style={{ "marginTop": "6px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "#4B5563" }}>
                      {t('trio.t19')}
                    </div>
                    {"\n"}
                  </div>
                  {"\n"}
                  <span style={{ "flex": "0 0 auto", "fontSize": "14.5px", "fontWeight": "700", "color": "#1A56DB" }}>
                    {t('trio.t20')}
                  </span>
                  {"\n"}
                </div>
                {"\n"}
              </div>
              {"\n"}
              <div className="hv2-timeline-row" style={{ "display": "grid", "gridTemplateColumns": "132px 34px 1fr", "alignItems": "stretch" }}>
                {"\n"}
                <div style={{ "padding": "22px 18px 0 0", "display": "flex", "flexDirection": "column", "alignItems": "flex-end", "gap": "5px" }}>
                  <div className="hv2-tlpill" data-active={tl2} style={{ "display": "inline-flex", "alignItems": "center", "gap": "7px", "background": "#F4F4F1", "border": "1px solid #E5E7EB", "borderRadius": "999px", "padding": "5px 11px 5px 9px" }}>
                    <span className="hv2-tlpin" data-active={tl2} aria-hidden="true" style={{ "width": "5px", "height": "5px", "borderRadius": "50%", "background": "#C3C7CE" }} />
                    <span className="hv2-tlbig" data-active={tl2} style={{ "fontSize": "12.5px", "fontWeight": "700", "letterSpacing": "-0.015em", "whiteSpace": "nowrap", "color": "#6B7280" }}>
                      {t('trio.t21')}
                    </span>
                  </div>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "lineHeight": "1.35", "textAlign": "right", "color": "#A8ACB4" }}>
                    {t('trio.t22')}
                  </div>
                </div>
                {"\n"}
                <div style={{ "position": "relative" }}>
                  <div className="hv2-tlseg" data-active={tl2} aria-hidden="true" style={{ "position": "absolute", "left": "11px", "top": "-12px", "bottom": "30px", "width": "2px", "background": "#E5E7EB" }} />
                  <span className="hv2-tldot" data-active={tl2} aria-hidden="true" style={{ "position": "absolute", "left": "0", "top": "20px", "width": "24px", "height": "24px", "borderRadius": "50%", "background": "#ffffff", "border": "2px solid #E5E7EB" }} />
                </div>
                {"\n"}
                <div className="hv2-tlcard" data-active={tl2} style={{ "background": "#ffffff", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "24px 28px", "display": "flex", "alignItems": "center", "gap": "18px", "boxShadow": "0 2px 12px rgba(0,0,0,0.03)" }}>
                  {"\n"}
                  <span style={{ "flex": "0 0 auto", "width": "56px", "height": "56px", "borderRadius": "15px", "overflow": "hidden", "background": "#F4F4F1" }}>
                    <HV3Slot id="ph-avocat-trio" src="/home-v3/avocat-trio.webp" hostStyle={{ "display": "block", "width": "56px", "height": "56px" }} frameStyle={{borderRadius: "12px"}} />
                  </span>
                  {"\n"}
                  <div style={{ "flex": "1 1 0%", "minWidth": "0" }}>
                    {"\n"}
                    <div style={{ "fontSize": "10.5px", "fontWeight": "700", "letterSpacing": "0.08em", "textTransform": "uppercase", "color": "#9CA3AF" }}>
                      {t('trio.t24')}
                    </div>
                    {"\n"}
                    <div style={{ "marginTop": "6px", "fontSize": "18px", "fontWeight": "700", "letterSpacing": "-0.024em" }}>
                      {t('trio.t25')}
                    </div>
                    {"\n"}
                    <div style={{ "marginTop": "6px", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "#4B5563" }}>
                      {t('trio.t26')}
                    </div>
                    {"\n"}
                  </div>
                  {"\n"}
                  <span style={{ "flex": "0 0 auto", "fontSize": "14.5px", "fontWeight": "700", "color": "#16a34a" }}>
                    {t('trio.t27')}
                  </span>
                  {"\n"}
                </div>
                {"\n"}
              </div>
              {"\n"}
            </div>
          </div>
        </section>
        {/* ═══ compare ═══ */}
        <section id="compare" style={{ "background": "rgb(244, 244, 241)", "padding": "88px 40px", "borderTop": "1px solid rgb(232, 232, 228)", "scrollMarginTop": "64px" }}>
          <div style={{ "maxWidth": "1000px", "margin": "0px auto" }}>
            <div style={{ "marginBottom": "40px", "maxWidth": "640px" }}>
              <h2 style={{ "fontSize": "clamp(38px, 5vw, 56px)", "fontWeight": "700", "letterSpacing": "-0.04em", "lineHeight": "1.04", "margin": "0px", "color": "rgb(10, 10, 15)" }}>
                {t('compare.t01')}
                <br />
                <span style={{ "background": "linear-gradient(135deg, rgb(26, 86, 219) 0%, rgb(30, 64, 175) 100%) text", "WebkitTextFillColor": "transparent", "color": "transparent" }}>
                  {t('compare.t02')}
                </span>
              </h2>
              <p style={{ "margin": "14px 0px 0px", "fontSize": "16px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgb(75, 85, 99)" }}>
                {t('compare.t03')}
              </p>
            </div>
            <div className="hv2-compare-row" style={{ "display": "flex", "alignItems": "flex-start", "gap": "20px", "maxWidth": "980px", "position": "relative" }}>
              <div className="hv2-compare-vs" style={{ "position": "absolute", "left": "50%", "top": "50%", "transform": "translate(-50%, -50%)", "zIndex": "2", "width": "42px", "height": "42px", "borderRadius": "50%", "background": "linear-gradient(135deg, rgb(26, 86, 219) 0%, rgb(30, 64, 175) 100%)", "border": "3px solid rgb(255, 255, 255)", "color": "rgb(255, 255, 255)", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "11.5px", "fontWeight": "800", "letterSpacing": "0.05em", "boxShadow": "rgba(26, 86, 219, 0.35) 0px 8px 24px" }}>
                {t('compare.t04')}
              </div>
              <div className="hv2-compare-old" style={{ "flex": "1 1 0px", "minWidth": "0px", "background": "rgb(250, 250, 248)", "border": "1px solid rgb(236, 236, 234)", "borderRadius": "18px", "padding": "22px 26px", "transition": "0.3s" }}>
                <div style={{ "height": "68px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "3px", "borderBottom": "1px solid rgb(229, 231, 235)" }}>
                  <div style={{ "fontSize": "12px", "fontWeight": "600", "letterSpacing": "0.08em", "textTransform": "uppercase", "color": "rgb(156, 163, 175)" }}>
                    {t('compare.t05')}
                  </div>
                  <div style={{ "fontSize": "20px", "fontWeight": "600", "letterSpacing": "-0.02em", "color": "rgb(107, 114, 128)" }}>
                    {t('compare.t06')}
                  </div>
                </div>
                <div style={{ "minHeight": "72px", "padding": "14px 0px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "5px", "borderBottom": "1px solid rgb(236, 236, 234)" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "letterSpacing": "0.07em", "textTransform": "uppercase", "color": "rgb(156, 163, 175)" }}>
                    {t('compare.t07')}
                  </div>
                  <div style={{ "display": "flex", "alignItems": "flex-start", "gap": "8px", "lineHeight": "1.35", "fontSize": "15.5px", "fontWeight": "500", "letterSpacing": "-0.01em", "color": "rgb(107, 114, 128)" }}>
                    <span style={{ "flex": "0 0 auto", "width": "20px", "height": "20px", "borderRadius": "50%", "background": "rgb(254, 226, 226)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" stroke="#dc2626" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </span>
                    <span style={{ "flex": "1 1 0%", "minWidth": "0px" }}>
                      {t('compare.t08')}
                    </span>
                  </div>
                </div>
                <div style={{ "minHeight": "72px", "padding": "14px 0px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "5px", "borderBottom": "1px solid rgb(236, 236, 234)" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "letterSpacing": "0.07em", "textTransform": "uppercase", "color": "rgb(156, 163, 175)" }}>
                    {t('compare.t09')}
                  </div>
                  <div style={{ "display": "flex", "alignItems": "flex-start", "gap": "8px", "lineHeight": "1.35", "fontSize": "15.5px", "fontWeight": "500", "letterSpacing": "-0.01em", "color": "rgb(107, 114, 128)" }}>
                    <span style={{ "flex": "0 0 auto", "width": "20px", "height": "20px", "borderRadius": "50%", "background": "rgb(254, 226, 226)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" stroke="#dc2626" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </span>
                    <span style={{ "flex": "1 1 0%", "minWidth": "0px" }}>
                      {t('compare.t10')}
                    </span>
                  </div>
                </div>
                <div style={{ "minHeight": "72px", "padding": "14px 0px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "5px", "borderBottom": "1px solid rgb(236, 236, 234)" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "letterSpacing": "0.07em", "textTransform": "uppercase", "color": "rgb(156, 163, 175)" }}>
                    {t('compare.t11')}
                  </div>
                  <div style={{ "display": "flex", "alignItems": "flex-start", "gap": "8px", "lineHeight": "1.35", "fontSize": "15.5px", "fontWeight": "500", "letterSpacing": "-0.01em", "color": "rgb(107, 114, 128)" }}>
                    <span style={{ "flex": "0 0 auto", "width": "20px", "height": "20px", "borderRadius": "50%", "background": "rgb(254, 226, 226)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" stroke="#dc2626" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </span>
                    <span style={{ "flex": "1 1 0%", "minWidth": "0px" }}>
                      {t('compare.t12')}
                    </span>
                  </div>
                </div>
                <div style={{ "minHeight": "72px", "padding": "14px 0px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "5px", "borderBottom": "1px solid rgb(236, 236, 234)" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "letterSpacing": "0.07em", "textTransform": "uppercase", "color": "rgb(156, 163, 175)" }}>
                    {t('compare.t13')}
                  </div>
                  <div style={{ "display": "flex", "alignItems": "flex-start", "gap": "8px", "lineHeight": "1.35", "fontSize": "15.5px", "fontWeight": "500", "letterSpacing": "-0.01em", "color": "rgb(107, 114, 128)" }}>
                    <span style={{ "flex": "0 0 auto", "width": "20px", "height": "20px", "borderRadius": "50%", "background": "rgb(254, 226, 226)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" stroke="#dc2626" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </span>
                    <span style={{ "flex": "1 1 0%", "minWidth": "0px" }}>
                      {t('compare.t14')}
                    </span>
                  </div>
                </div>
                <div style={{ "minHeight": "72px", "padding": "14px 0px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "5px", "borderBottomWidth": "medium", "borderBottomStyle": "none", "borderBottomColor": "currentcolor" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "letterSpacing": "0.07em", "textTransform": "uppercase", "color": "rgb(156, 163, 175)" }}>
                    {t('compare.t15')}
                  </div>
                  <div style={{ "display": "flex", "alignItems": "flex-start", "gap": "8px", "lineHeight": "1.35", "fontSize": "15.5px", "fontWeight": "500", "letterSpacing": "-0.01em", "color": "rgb(107, 114, 128)" }}>
                    <span style={{ "flex": "0 0 auto", "width": "20px", "height": "20px", "borderRadius": "50%", "background": "rgb(254, 226, 226)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" stroke="#dc2626" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </span>
                    <span style={{ "flex": "1 1 0%", "minWidth": "0px" }}>
                      {t('compare.t16')}
                    </span>
                  </div>
                </div>
              </div>
              <div className="hv2-compare-new" style={{ "flex": "1 1 0px", "minWidth": "0px", "background": "rgb(255, 255, 255)", "border": "2px solid rgb(26, 86, 219)", "borderRadius": "18px", "boxShadow": "rgba(26, 86, 219, 0.16) 0px 24px 56px", "padding": "22px 26px", "transition": "0.3s" }}>
                <div style={{ "height": "68px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "3px", "borderBottom": "1px solid rgb(229, 231, 235)" }}>
                  <div style={{ "fontSize": "12px", "fontWeight": "600", "letterSpacing": "0.08em", "textTransform": "uppercase", "color": "rgb(10, 10, 15)" }}>
                    {t('compare.t17')}
                  </div>
                  <div style={{ "display": "flex", "alignItems": "center", "gap": "2px", "fontSize": "17px", "fontWeight": "700", "letterSpacing": "-0.02em", "color": "rgb(10, 10, 15)" }}>
                    <img alt={t('compare.t18')} src="/home-v3/logo.svg" style={{ "height": "28px", "width": "auto", "display": "block" }} />
                    <span style={{ "fontSize": "22px", "lineHeight": "1", "marginLeft": "-28px" }}>
                      {t('compare.t19')}
                    </span>
                  </div>
                </div>
                <div style={{ "minHeight": "72px", "padding": "14px 0px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "5px", "borderBottom": "1px solid rgb(244, 244, 241)" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "letterSpacing": "0.07em", "textTransform": "uppercase", "color": "rgb(156, 163, 175)" }}>
                    {t('compare.t20')}
                  </div>
                  <div style={{ "display": "flex", "alignItems": "flex-start", "gap": "8px", "lineHeight": "1.35", "fontSize": "16.5px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)" }}>
                    <span style={{ "flex": "0 0 auto", "width": "20px", "height": "20px", "borderRadius": "50%", "background": "rgb(220, 252, 231)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span style={{ "flex": "1 1 0%", "minWidth": "0px" }}>
                      {t('compare.t21')}
                      <span style={{ "display": "inline-block", "marginLeft": "8px", "verticalAlign": "middle", "background": "rgb(240, 253, 244)", "border": "1px solid rgb(220, 252, 231)", "color": "rgb(21, 128, 61)", "fontSize": "11px", "fontWeight": "600", "padding": "3px 9px", "borderRadius": "999px", "letterSpacing": "0px", "whiteSpace": "nowrap" }}>
                        {t('compare.t22')}
                      </span>
                    </span>
                  </div>
                </div>
                <div style={{ "minHeight": "72px", "padding": "14px 0px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "5px", "borderBottom": "1px solid rgb(244, 244, 241)" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "letterSpacing": "0.07em", "textTransform": "uppercase", "color": "rgb(156, 163, 175)" }}>
                    {t('compare.t23')}
                  </div>
                  <div style={{ "display": "flex", "alignItems": "flex-start", "gap": "8px", "lineHeight": "1.35", "fontSize": "16.5px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)" }}>
                    <span style={{ "flex": "0 0 auto", "width": "20px", "height": "20px", "borderRadius": "50%", "background": "rgb(220, 252, 231)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span style={{ "flex": "1 1 0%", "minWidth": "0px" }}>
                      {t('compare.t24')}
                    </span>
                  </div>
                </div>
                <div style={{ "minHeight": "72px", "padding": "14px 0px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "5px", "borderBottom": "1px solid rgb(244, 244, 241)" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "letterSpacing": "0.07em", "textTransform": "uppercase", "color": "rgb(156, 163, 175)" }}>
                    {t('compare.t25')}
                  </div>
                  <div style={{ "display": "flex", "alignItems": "flex-start", "gap": "8px", "lineHeight": "1.35", "fontSize": "16.5px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)" }}>
                    <span style={{ "flex": "0 0 auto", "width": "20px", "height": "20px", "borderRadius": "50%", "background": "rgb(220, 252, 231)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span style={{ "flex": "1 1 0%", "minWidth": "0px", "display": "flex", "alignItems": "center", "gap": "9px" }}>
                      <span style={{ "flex": "0 0 auto", "width": "26px", "height": "26px", "borderRadius": "50%", "overflow": "hidden", "background": "#F4F4F1", "boxShadow": "0 0 0 2px #ffffff, 0 0 0 3px #ECECEA" }}>
                        <HV3Slot id="ph-camille-compare" data-mini="" src="/home-v3/camille-compare.jpg" hostStyle={{ "display": "block", "width": "26px", "height": "26px" }} frameStyle={{borderRadius: "50%"}} />
                      </span>
                      <span style={{ "minWidth": "0" }}>
                        {t('compare.t27')}
                      </span>
                    </span>
                  </div>
                </div>
                <div style={{ "minHeight": "72px", "padding": "14px 0px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "5px", "borderBottom": "1px solid rgb(244, 244, 241)" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "letterSpacing": "0.07em", "textTransform": "uppercase", "color": "rgb(156, 163, 175)" }}>
                    {t('compare.t28')}
                  </div>
                  <div style={{ "display": "flex", "alignItems": "flex-start", "gap": "8px", "lineHeight": "1.35", "fontSize": "16.5px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)" }}>
                    <span style={{ "flex": "0 0 auto", "width": "20px", "height": "20px", "borderRadius": "50%", "background": "rgb(220, 252, 231)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span style={{ "flex": "1 1 0%", "minWidth": "0px" }}>
                      {t('compare.t29')}
                    </span>
                  </div>
                </div>
                <div style={{ "minHeight": "72px", "padding": "14px 0px", "display": "flex", "flexDirection": "column", "justifyContent": "center", "gap": "5px", "borderBottomWidth": "medium", "borderBottomStyle": "none", "borderBottomColor": "currentcolor" }}>
                  <div style={{ "fontSize": "11px", "fontWeight": "500", "letterSpacing": "0.07em", "textTransform": "uppercase", "color": "rgb(156, 163, 175)" }}>
                    {t('compare.t30')}
                  </div>
                  <div style={{ "display": "flex", "alignItems": "flex-start", "gap": "8px", "lineHeight": "1.35", "fontSize": "16.5px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)" }}>
                    <span style={{ "flex": "0 0 auto", "width": "20px", "height": "20px", "borderRadius": "50%", "background": "rgb(220, 252, 231)", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span style={{ "flex": "1 1 0%", "minWidth": "0px" }}>
                      {t('compare.t31')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <p className="hv2-compare-closing" data-testid="home-compare-closing" style={{ "maxWidth": "980px", "margin": "30px 0px 0px", "fontFamily": "\"Inter Tight\", -apple-system, BlinkMacSystemFont, system-ui, sans-serif", "fontStyle": "normal", "fontWeight": "600", "fontSize": "clamp(18px, 2.04vw, 24px)", "lineHeight": "1.3", "letterSpacing": "-0.02em", "color": "rgb(10, 10, 15)", "textAlign": "center" }}>
              {t('compare.t32')}
            </p>
          </div>
        </section>
        {/* ═══ mission ═══ */}
        <section id="chatbot" style={{ "background": "rgb(255, 255, 255)", "borderTop": "1px solid rgb(232, 232, 228)", "padding": "76px 40px" }}>
          {"\n"}
          <div style={{ "maxWidth": "1000px", "margin": "0px auto", "background": "#F5F8FF", "border": "1px solid #E2EAFB", "borderRadius": "20px", "padding": "clamp(22px, 2.6vw, 32px)", "display": "grid", "gridTemplateColumns": "minmax(0, 1.15fr) minmax(260px, 0.85fr)", "gap": "clamp(28px, 4vw, 48px)", "alignItems": "center" }} className="hv2-mission-grid">
            {"\n"}
            <div style={{ "display": "flex", "flexDirection": "column", "justifyContent": "center" }}>
              {"\n"}
              <div className="hv2-eyebrow" style={{ "marginBottom": "12px" }}>
                {t('mission.t01')}
              </div>
              {"\n"}
              <h2 style={{ "margin": "0", "maxWidth": "16ch", "fontFamily": "'Inter Tight', -apple-system, sans-serif", "fontSize": "clamp(24px, 2.7vw, 32px)", "fontWeight": "700", "letterSpacing": "-0.032em", "lineHeight": "1.1", "color": "#0a0a0f" }}>
                {t('mission.t02')}
              </h2>
              {"\n"}
              <p style={{ "textAlign": "justify", "hyphens": "auto", "margin": "16px 0 0", "maxWidth": "46ch", "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.65", "color": "#3F4652" }}>
                {t('mission.t03')}
              </p>
              {"\n"}
              <div style={{ "marginTop": "24px", "display": "flex", "alignItems": "center", "flexWrap": "wrap", "gap": "12px 14px" }}>
                {"\n"}
                <span aria-label={t('mission.t04')} style={{ "fontFamily": "'Inter Tight', -apple-system, sans-serif", "fontSize": "18px", "fontWeight": "800", "letterSpacing": "-0.04em", "lineHeight": "1", "color": "#1A56DB" }}>
                  {t('mission.t05')}
                </span>
                {"\n"}
                <span aria-hidden="true" style={{ "width": "1px", "height": "16px", "background": "#C9D6F2" }} />
                {"\n"}
                <span style={{ "fontFamily": "'Inter Tight', -apple-system, sans-serif", "fontSize": "17px", "fontWeight": "700", "fontStyle": "italic", "letterSpacing": "-0.01em", "lineHeight": "1.2", "color": "#3F4652" }}>
                  {t('mission.t06')}
                </span>
                {"\n"}
              </div>
              {"\n"}
            </div>
            {"\n"}
            <div>
              <div style={{ "borderRadius": "16px", "overflow": "hidden", "background": "#E9E9E4", "height": "300px", "boxShadow": "0 18px 44px rgba(10,10,15,0.10)" }}>
                <HV3Slot id="ph-camille-chatbot" src="/home-v3/mission-juriste.webp" hostStyle={{ "display": "block", "width": "100%", "height": "100%" }} frameStyle={{}} />
              </div>
            </div>
            {"\n"}
          </div>
          {"\n"}
        </section>
        {/* ═══ journey ═══ */}
        <section id="journey" style={{ "background": "rgb(244, 244, 241)", "padding": "88px 40px 44px", "borderTop": "1px solid rgb(232, 232, 228)", "scrollMarginTop": "64px" }}>
          <div style={{ "maxWidth": "1000px", "margin": "0px auto" }}>
            <div style={{ "marginBottom": "36px", "maxWidth": "600px" }}>
              <h2 style={{ "fontSize": "clamp(34px, 4.6vw, 52px)", "fontWeight": "600", "letterSpacing": "-0.035em", "lineHeight": "1.08", "margin": "0px", "color": "rgb(10, 10, 15)" }}>
                {t('journey.t01')}
              </h2>
              <div style={{ "display": "flex", "alignItems": "center", "gap": "8px", "fontSize": "clamp(17px, 1.8vw, 21px)", "fontWeight": "600", "letterSpacing": "-0.02em", "color": "rgb(26, 86, 219)", "marginTop": "14px" }}>
                {t('journey.t02')}
              </div>
              <p style={{ "margin": "12px 0px 0px", "fontSize": "16px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgb(75, 85, 99)" }}>
                {t('journey.t03')}
              </p>
            </div>
            <div className="hv2-steps-grid" style={{ "display": "grid", "gridTemplateColumns": "repeat(4, 1fr)", "gap": "12px" }}>
              <button type="button" className="hv2-stepcard" aria-pressed={step === 0 ? "true" : "false"} onClick={() => setStep(0)} style={cardStyle(0)}>
                <div style={{ "display": "flex", "alignItems": "center", "justifyContent": "space-between", "marginBottom": "18px" }}>
                  <span style={{ "fontSize": "14px", "fontWeight": "700", "letterSpacing": "0.02em", "color": "rgb(26, 86, 219)" }}>
                    {t('journey.t04')}
                  </span>
                  <div style={{ "width": "40px", "height": "40px", "borderRadius": "11px", "background": "rgb(239, 246, 255)", "display": "flex", "alignItems": "center", "justifyContent": "center", "color": "rgb(26, 86, 219)" }}>
                    <svg width="19" height="19" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </div>
                </div>
                <div style={{ "fontSize": "16px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)", "marginBottom": "4px" }}>
                  {t('journey.t05')}
                </div>
                <div style={{ "fontSize": "13.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgb(107, 114, 128)" }}>
                  {t('journey.t06')}
                </div>
              </button>
              <button type="button" className="hv2-stepcard" aria-pressed={step === 1 ? "true" : "false"} onClick={() => setStep(1)} style={cardStyle(1)}>
                <div style={{ "display": "flex", "alignItems": "center", "justifyContent": "space-between", "marginBottom": "18px" }}>
                  <span style={{ "fontSize": "14px", "fontWeight": "700", "letterSpacing": "0.02em", "color": "rgb(124, 58, 237)" }}>
                    {t('journey.t07')}
                  </span>
                  <div style={{ "width": "40px", "height": "40px", "borderRadius": "11px", "background": "rgb(243, 232, 255)", "display": "flex", "alignItems": "center", "justifyContent": "center", "overflow": "hidden" }}>
                    <img alt="" aria-hidden="true" src="/home-v3/hero-owl.png" style={{ "width": "30px", "height": "30px", "objectFit": "contain", "display": "block" }} />
                  </div>
                </div>
                <div style={{ "fontSize": "16px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)", "marginBottom": "4px" }}>
                  {t('journey.t08')}
                </div>
                <div style={{ "fontSize": "13.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgb(107, 114, 128)" }}>
                  {t('journey.t09')}
                </div>
              </button>
              <div role="button" tabIndex={0} className="hv2-stepcard" aria-pressed={step === 2 ? "true" : "false"} onClick={() => setStep(2)} style={cardStyle(2)}>
                {"\n"}
                <div style={{ "display": "flex", "alignItems": "center", "justifyContent": "space-between", "marginBottom": "18px" }}>
                  {"\n"}
                  <span style={{ "fontSize": "14px", "fontWeight": "700", "letterSpacing": "0.02em", "color": "rgb(180, 83, 9)" }}>
                    {t('journey.t10')}
                  </span>
                  {"\n"}
                  <span style={{ "flex": "0 0 auto", "width": "40px", "height": "40px", "borderRadius": "11px", "overflow": "hidden", "background": "#F4F4F1", "display": "block", "cursor": "pointer" }}>
                    <HV3Slot id="ph-step-juriste" src="/home-v3/etape-juriste.webp" hostStyle={{ "display": "block", "width": "40px", "height": "40px" }} frameStyle={{borderRadius: "12px"}} />
                  </span>
                  {"\n"}
                </div>
                {"\n"}
                <div style={{ "fontSize": "16px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)", "marginBottom": "4px" }}>
                  {t('journey.t12')}
                </div>
                {"\n"}
                <div style={{ "fontSize": "13.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgb(107, 114, 128)" }}>
                  {t('journey.t13')}
                </div>
                {"\n"}
              </div>
              <button type="button" className="hv2-stepcard-green" aria-pressed={step === 3 ? "true" : "false"} onClick={() => setStep(3)} style={cardStyle(3)}>
                <div style={{ "display": "flex", "alignItems": "center", "justifyContent": "space-between", "marginBottom": "18px" }}>
                  <span style={{ "fontSize": "14px", "fontWeight": "700", "letterSpacing": "0.02em", "color": "rgb(22, 163, 74)" }}>
                    {t('journey.t14')}
                  </span>
                  <span style={{ "flex": "0 0 auto", "width": "40px", "height": "40px", "borderRadius": "11px", "background": "rgb(240, 253, 244)", "display": "flex", "alignItems": "center", "justifyContent": "center", "fontSize": "21px", "lineHeight": "1" }}>
                    {t('journey.t15')}
                  </span>
                </div>
                <div style={{ "fontSize": "16px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)", "marginBottom": "4px" }}>
                  {t('journey.t16')}
                </div>
                <div style={{ "fontSize": "13.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgb(107, 114, 128)" }}>
                  {t('journey.t17')}
                </div>
              </button>
            </div>
            <div style={{ "marginTop": "12px", "background": "#ffffff", "border": "1px solid #ECECEA", "borderRadius": "16px", "padding": "26px 28px", "display": "flex", "gap": "32px", "alignItems": "center", "flexWrap": "wrap" }}>
              {"\n"}
              <div style={{ "flex": "1 1 0%", "minWidth": "280px" }}>
                {"\n"}
                <div style={{ "display": "flex", "alignItems": "center", "gap": "9px", "marginBottom": "7px" }}>
                  <span style={{ "fontSize": "12px", "fontWeight": "500", "color": "#9CA3AF" }}>
                    {st.num}
                  </span>
                  <div style={{ "fontSize": "16px", "fontWeight": "600", "letterSpacing": "-0.02em", "color": "#0a0a0f" }}>
                    {st.title}
                  </div>
                </div>
                {"\n"}
                <div style={{ "fontSize": "14.5px", "fontWeight": "400", "lineHeight": "1.65", "color": "#4B5563", "maxWidth": "560px" }}>
                  {st.body}
                </div>
                {"\n"}
                <div style={{ "marginTop": "12px", "fontSize": "12.5px", "fontWeight": "400", "color": "#9CA3AF" }}>
                  {t('journey.t18')}
                  <span style={{ "fontWeight": "600", "color": "#0a0a0f" }}>
                    {st.meta}
                  </span>
                </div>
                {"\n"}
              </div>
              {"\n"}
              <div style={{ "flex": "0 0 300px", "minWidth": "260px" }}>
                {"\n"}
                {isStep0 && (
                  <>
                  {"\n"}
                  <div style={{ "background": "#ffffff", "border": "1.5px dashed #BFDBFE", "borderRadius": "12px", "padding": "22px 20px", "display": "flex", "flexDirection": "column", "alignItems": "center", "gap": "10px" }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" stroke="#1a56db" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <div style={{ "display": "inline-flex", "alignItems": "center", "gap": "8px", "background": "#FAFAF8", "border": "1px solid #ECECEA", "borderRadius": "8px", "padding": "8px 12px", "fontSize": "12.5px", "fontWeight": "500", "color": "#374151" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      {t('journey.t19')}
                      <span style={{ "fontSize": "11px", "color": "#9CA3AF" }}>
                        {t('journey.t20')}
                      </span>
                    </div>
                  </div>
                  {"\n"}
                  </>
                )}
                {"\n"}
                {isStep1 && (
                  <>
                  {"\n"}
                  <div style={{ "background": "#ffffff", "border": "1px solid #ECECEA", "borderRadius": "12px", "padding": "18px 18px 16px", "boxShadow": "0 8px 24px rgba(10,10,15,0.05)" }}>
                    {"\n"}
                    <div style={{ "fontSize": "9.5px", "fontWeight": "700", "letterSpacing": "0.09em", "textTransform": "uppercase", "color": "#9CA3AF" }}>
                      {t('journey.t21')}
                    </div>
                    {"\n"}
                    <div style={{ "display": "flex", "flexDirection": "column", "gap": "8px", "marginTop": "12px" }}>
                      {"\n"}
                      <div style={{ "display": "flex", "alignItems": "center", "gap": "8px", "fontSize": "12.5px", "fontWeight": "500", "color": "#0a0a0f" }}>
                        <span style={{ "width": "6px", "height": "6px", "borderRadius": "50%", "background": "#16a34a" }} />
                        {t('journey.t22')}
                      </div>
                      {"\n"}
                      <div style={{ "display": "flex", "alignItems": "center", "gap": "8px", "fontSize": "12.5px", "fontWeight": "500", "color": "#0a0a0f" }}>
                        <span style={{ "width": "6px", "height": "6px", "borderRadius": "50%", "background": "#16a34a" }} />
                        {t('journey.t23')}
                      </div>
                      {"\n"}
                      <div style={{ "display": "flex", "alignItems": "center", "gap": "8px", "fontSize": "12.5px", "fontWeight": "500", "color": "#1A56DB" }}>
                        <span style={{ "width": "6px", "height": "6px", "borderRadius": "50%", "background": "#1A56DB", "animation": "hv2LiveDot 1.6s infinite" }} />
                        {t('journey.t24')}
                      </div>
                      {"\n"}
                      <div style={{ "display": "flex", "alignItems": "center", "gap": "8px", "fontSize": "12.5px", "fontWeight": "400", "color": "#9CA3AF" }}>
                        <span style={{ "width": "6px", "height": "6px", "borderRadius": "50%", "background": "#E5E7EB" }} />
                        {t('journey.t25')}
                      </div>
                      {"\n"}
                    </div>
                    {"\n"}
                    <div style={{ "height": "1px", "background": "#F1F1EE", "margin": "13px 0 11px" }} />
                    {"\n"}
                    <div style={{ "display": "inline-flex", "alignItems": "center", "gap": "6px", "background": "#F8F8F5", "border": "1px solid #ECECEA", "borderRadius": "7px", "padding": "5px 9px", "fontFamily": "'JetBrains Mono', monospace", "fontSize": "9.5px", "fontWeight": "500", "color": "#6B7280" }}>
                      {t('journey.t26')}
                    </div>
                    {"\n"}
                  </div>
                  {"\n"}
                  </>
                )}
                {"\n"}
                {isStep2 && (
                  <>
                  {"\n"}
                  <div style={{ "background": "#ffffff", "border": "1px solid #ECECEA", "borderRadius": "12px", "padding": "18px", "boxShadow": "0 8px 24px rgba(10,10,15,0.05)" }}>
                    {"\n"}
                    <div style={{ "fontSize": "9.5px", "fontWeight": "700", "letterSpacing": "0.09em", "textTransform": "uppercase", "color": "#9CA3AF" }}>
                      {t('journey.t27')}
                    </div>
                    {"\n"}
                    <div style={{ "display": "flex", "flexDirection": "column", "gap": "6px", "margin": "12px 0 13px" }}>
                      <span style={{ "height": "6px", "borderRadius": "3px", "background": "#EFEFEC", "width": "94%" }} />
                      <span style={{ "height": "6px", "borderRadius": "3px", "background": "#EFEFEC", "width": "100%" }} />
                      <span style={{ "height": "6px", "borderRadius": "3px", "background": "#EFEFEC", "width": "76%" }} />
                      <span style={{ "height": "6px", "borderRadius": "3px", "background": "#EFEFEC", "width": "90%" }} />
                      <span style={{ "height": "6px", "borderRadius": "3px", "background": "#EFEFEC", "width": "58%" }} />
                    </div>
                    {"\n"}
                    <div style={{ "height": "1px", "background": "#F1F1EE", "marginBottom": "12px" }} />
                    {"\n"}
                    <div style={{ "display": "flex", "alignItems": "center", "gap": "10px" }}>
                      {"\n"}
                      <span style={{ "flex": "0 0 auto", "width": "38px", "height": "38px", "borderRadius": "50%", "overflow": "hidden", "background": "#F4F4F1" }}>
                        <HV3Slot id="ph-camille-step4" data-mini="" src="/home-v3/camille-etape4.png" hostStyle={{ "display": "block", "width": "38px", "height": "38px" }} frameStyle={{borderRadius: "50%"}} />
                      </span>
                      {"\n"}
                      <span style={{ "minWidth": "0" }}>
                        <span style={{ "display": "block", "fontSize": "13px", "fontWeight": "700", "letterSpacing": "-0.015em", "color": "#0a0a0f" }}>
                          {t('journey.t29')}
                        </span>
                        <span style={{ "display": "block", "fontSize": "11px", "fontWeight": "500", "color": "#6B7280" }}>
                          {t('journey.t30')}
                        </span>
                      </span>
                      {"\n"}
                    </div>
                    {"\n"}
                    <div style={{ "marginTop": "11px", "display": "inline-flex", "alignItems": "center", "gap": "5px", "background": "#F0FDF4", "border": "1px solid #DCFCE7", "color": "#15803D", "fontSize": "10.5px", "fontWeight": "700", "padding": "4px 10px", "borderRadius": "999px" }}>
                      <svg width="9" height="9" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {t('journey.t31')}
                    </div>
                    {"\n"}
                  </div>
                  {"\n"}
                  </>
                )}
                {"\n"}
                {isStep3 && (
                  <>
                  {"\n"}
                  <div style={{ "background": "#ffffff", "border": "1px solid #ECECEA", "borderRadius": "12px", "padding": "18px", "boxShadow": "0 8px 24px rgba(10,10,15,0.05)" }}>
                    {"\n"}
                    <div style={{ "fontSize": "9.5px", "fontWeight": "700", "letterSpacing": "0.09em", "textTransform": "uppercase", "color": "#9CA3AF" }}>
                      {t('journey.t32')}
                    </div>
                    {"\n"}
                    <div style={{ "display": "flex", "flexDirection": "column", "gap": "9px", "marginTop": "13px" }}>
                      {"\n"}
                      <div style={{ "display": "flex", "alignItems": "center", "gap": "8px", "fontSize": "12.5px", "fontWeight": "500", "color": "#0a0a0f" }}>
                        <span style={{ "flex": "0 0 auto", "width": "16px", "height": "16px", "borderRadius": "50%", "background": "#DCFCE7", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                          <svg width="8" height="8" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                        {t('journey.t33')}
                      </div>
                      {"\n"}
                      <div style={{ "display": "flex", "alignItems": "center", "gap": "8px", "fontSize": "12.5px", "fontWeight": "500", "color": "#0a0a0f" }}>
                        <span style={{ "flex": "0 0 auto", "width": "16px", "height": "16px", "borderRadius": "50%", "background": "#DCFCE7", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                          <svg width="8" height="8" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                        {t('journey.t34')}
                      </div>
                      {"\n"}
                      <div style={{ "display": "flex", "alignItems": "center", "gap": "8px", "fontSize": "12.5px", "fontWeight": "600", "color": "#15803D" }}>
                        <span style={{ "flex": "0 0 auto", "width": "16px", "height": "16px", "borderRadius": "50%", "background": "#DCFCE7", "display": "flex", "alignItems": "center", "justifyContent": "center" }}>
                          <svg width="8" height="8" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                        {t('journey.t35')}
                      </div>
                      {"\n"}
                    </div>
                    {"\n"}
                    <div style={{ "height": "1px", "background": "#F1F1EE", "margin": "14px 0 13px" }} />
                    {"\n"}
                    <div style={{ "display": "flex", "alignItems": "center", "justifyContent": "space-between", "gap": "12px" }}>
                      {"\n"}
                      <span style={{ "display": "inline-flex", "alignItems": "center", "gap": "6px", "border": "1.5px solid #16a34a", "color": "#15803D", "fontFamily": "'JetBrains Mono', monospace", "fontSize": "10.5px", "fontWeight": "700", "letterSpacing": "0.1em", "padding": "5px 11px", "borderRadius": "6px", "transform": "rotate(-1.5deg)" }}>
                        {t('journey.t36')}
                      </span>
                      {"\n"}
                      <span style={{ "fontSize": "11.5px", "fontWeight": "500", "color": "#9CA3AF" }}>
                        {t('journey.t37')}
                      </span>
                      {"\n"}
                    </div>
                    {"\n"}
                  </div>
                  {"\n"}
                  </>
                )}
                {"\n"}
              </div>
              {"\n"}
            </div>
          </div>
        </section>
        {/* ═══ cases ═══ */}
        <section id="cases" style={{ "background": "rgb(255, 255, 255)", "padding": "56px 40px 88px", "borderTop": "1px solid rgb(232, 232, 228)", "scrollMarginTop": "64px" }}>
          <div style={{ "maxWidth": "1000px", "margin": "0px auto" }}>
            <div style={{ "marginBottom": "36px", "maxWidth": "600px" }}>
              <h2 style={{ "fontSize": "clamp(34px, 4.6vw, 52px)", "fontWeight": "600", "letterSpacing": "-0.035em", "lineHeight": "1.08", "margin": "0px", "color": "rgb(10, 10, 15)" }}>
                {t('cases.t01')}
              </h2>
              <div style={{ "fontSize": "clamp(17px, 1.8vw, 21px)", "fontWeight": "600", "letterSpacing": "-0.02em", "color": "rgb(26, 86, 219)", "marginTop": "14px" }}>
                {t('cases.t02')}
              </div>
              <p style={{ "margin": "12px 0px 0px", "fontSize": "16px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgb(75, 85, 99)" }}>
                {t('cases.t03')}
              </p>
            </div>
            <div className="hv2-cases-grid" style={{ "display": "grid", "gridTemplateColumns": "repeat(3, 1fr)", "gap": "12px" }}>
              <div className="hv2-casecard" style={{ "background": "rgb(255, 255, 255)", "border": "1px solid rgb(229, 231, 235)", "borderRadius": "14px", "padding": "22px 20px", "transition": "0.18s" }}>
                <div style={{ "width": "34px", "height": "34px", "borderRadius": "9px", "background": "rgb(254, 226, 226)", "display": "flex", "alignItems": "center", "justifyContent": "center", "color": "rgb(185, 28, 28)", "marginBottom": "14px" }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                </div>
                <div style={{ "fontSize": "15px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)", "marginBottom": "4px" }}>
                  {t('cases.t04')}
                </div>
                <div style={{ "fontSize": "13.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgb(107, 114, 128)" }}>
                  {t('cases.t05')}
                </div>
              </div>
              <div className="hv2-casecard" style={{ "background": "rgb(255, 255, 255)", "border": "1px solid rgb(229, 231, 235)", "borderRadius": "14px", "padding": "22px 20px", "transition": "0.18s" }}>
                <div style={{ "width": "34px", "height": "34px", "borderRadius": "9px", "background": "rgb(254, 243, 199)", "display": "flex", "alignItems": "center", "justifyContent": "center", "color": "rgb(180, 83, 9)", "marginBottom": "14px" }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                </div>
                <div style={{ "fontSize": "15px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)", "marginBottom": "4px" }}>
                  {t('cases.t06')}
                </div>
                <div style={{ "fontSize": "13.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgb(107, 114, 128)" }}>
                  {t('cases.t07')}
                </div>
              </div>
              <div className="hv2-casecard" style={{ "background": "rgb(255, 255, 255)", "border": "1px solid rgb(229, 231, 235)", "borderRadius": "14px", "padding": "22px 20px", "transition": "0.18s" }}>
                <div style={{ "width": "34px", "height": "34px", "borderRadius": "9px", "background": "rgb(239, 246, 255)", "display": "flex", "alignItems": "center", "justifyContent": "center", "color": "rgb(26, 86, 219)", "marginBottom": "14px" }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 17h4V5H2v12h3" />
                    <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1" />
                    <circle cx="7.5" cy="17.5" r="2.5" />
                    <circle cx="17.5" cy="17.5" r="2.5" />
                  </svg>
                </div>
                <div style={{ "fontSize": "15px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)", "marginBottom": "4px" }}>
                  {t('cases.t08')}
                </div>
                <div style={{ "fontSize": "13.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgb(107, 114, 128)" }}>
                  {t('cases.t09')}
                </div>
              </div>
              <div className="hv2-casecard" style={{ "background": "rgb(255, 255, 255)", "border": "1px solid rgb(229, 231, 235)", "borderRadius": "14px", "padding": "22px 20px", "transition": "0.18s" }}>
                <div style={{ "width": "34px", "height": "34px", "borderRadius": "9px", "background": "rgb(243, 232, 255)", "display": "flex", "alignItems": "center", "justifyContent": "center", "color": "rgb(124, 58, 237)", "marginBottom": "14px" }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                  </svg>
                </div>
                <div style={{ "fontSize": "15px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)", "marginBottom": "4px" }}>
                  {t('cases.t10')}
                </div>
                <div style={{ "fontSize": "13.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgb(107, 114, 128)" }}>
                  {t('cases.t11')}
                </div>
              </div>
              <div className="hv2-casecard" style={{ "background": "rgb(255, 255, 255)", "border": "1px solid rgb(229, 231, 235)", "borderRadius": "14px", "padding": "22px 20px", "transition": "0.18s" }}>
                <div style={{ "width": "34px", "height": "34px", "borderRadius": "9px", "background": "rgb(240, 253, 244)", "display": "flex", "alignItems": "center", "justifyContent": "center", "color": "rgb(22, 163, 74)", "marginBottom": "14px" }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8" cy="21" r="1" />
                    <circle cx="19" cy="21" r="1" />
                    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
                  </svg>
                </div>
                <div style={{ "fontSize": "15px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)", "marginBottom": "4px" }}>
                  {t('cases.t12')}
                </div>
                <div style={{ "fontSize": "13.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgb(107, 114, 128)" }}>
                  {t('cases.t13')}
                </div>
              </div>
              <div className="hv2-casecard" style={{ "background": "rgb(255, 255, 255)", "border": "1px solid rgb(229, 231, 235)", "borderRadius": "14px", "padding": "22px 20px", "transition": "0.18s" }}>
                <div style={{ "width": "34px", "height": "34px", "borderRadius": "9px", "background": "rgb(236, 254, 255)", "display": "flex", "alignItems": "center", "justifyContent": "center", "color": "rgb(8, 145, 178)", "marginBottom": "14px" }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>
                <div style={{ "fontSize": "15px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)", "marginBottom": "4px" }}>
                  {t('cases.t14')}
                </div>
                <div style={{ "fontSize": "13.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgb(107, 114, 128)" }}>
                  {t('cases.t15')}
                </div>
              </div>
            </div>
          </div>
          <div style={{ "maxWidth": "1000px", "margin": "26px auto 0px" }}>
            <div style={{ "border": "1.5px dashed rgb(199, 214, 244)", "background": "rgb(246, 249, 255)", "borderRadius": "14px", "padding": "22px 26px", "display": "flex", "gap": "18px", "alignItems": "center", "flexWrap": "wrap" }}>
              <div style={{ "flex": "1 1 0%", "minWidth": "260px" }}>
                <div style={{ "fontSize": "16px", "fontWeight": "600", "letterSpacing": "-0.015em", "color": "rgb(10, 10, 15)", "marginBottom": "4px" }}>
                  {t('cases.t16')}
                </div>
                <div style={{ "fontSize": "13.5px", "fontWeight": "400", "lineHeight": "1.55", "color": "rgb(75, 85, 99)" }}>
                  {t('cases.t17')}
                </div>
              </div>
              <button onClick={goStart} type="button" className="hv2-btn-primary" style={{ "flex": "0 0 auto", "padding": "11px 18px", "background": "rgb(26, 86, 219)", "color": "rgb(255, 255, 255)", "borderWidth": "medium", "borderStyle": "none", "borderColor": "currentcolor", "borderImage": "none", "borderRadius": "10px", "fontSize": "14px", "fontWeight": "600", "letterSpacing": "-0.01em", "fontFamily": "inherit", "cursor": "pointer", "transition": "background 0.15s" }}>
                {t('cases.t18')}
              </button>
            </div>
          </div>
        </section>
        {/* ═══ equipe ═══ */}
        <section id="equipe" style={{ "background": "rgb(255, 255, 255)", "borderTop": "1px solid rgb(232, 232, 228)", "padding": "84px 40px" }}>
          {"\n"}
          <div style={{ "maxWidth": "1000px", "margin": "0px auto", "display": "flex", "gap": "48px", "flexWrap": "wrap", "alignItems": "center" }}>
            {"\n"}
            <div style={{ "flex": "1 1 340px", "minWidth": "290px" }}>
              {"\n"}
              <div style={{ "borderRadius": "18px", "overflow": "hidden", "background": "#E9E9E4", "height": "340px", "boxShadow": "0 18px 44px rgba(10,10,15,0.10)" }}>
                <HV3Slot id="ph-fondateurs" src="/home-v3/fondateurs.webp" hostStyle={{ "display": "block", "width": "100%", "height": "100%" }} frameStyle={{}} />
              </div>
              {"\n"}
            </div>
            {"\n"}
            <div style={{ "flex": "1 1 420px", "minWidth": "300px" }}>
              {"\n"}
              <div className="hv2-eyebrow" style={{ "marginBottom": "10px" }}>
                {t('equipe.t01')}
              </div>
              {"\n"}
              <h2 style={{ "margin": "0", "fontFamily": "'Inter Tight', -apple-system, sans-serif", "fontSize": "clamp(24px, 2.8vw, 32px)", "fontWeight": "700", "letterSpacing": "-0.03em", "lineHeight": "1.15", "color": "#0a0a0f" }}>
                {t('equipe.t02')}
              </h2>
              {"\n"}
              <div style={{ "margin": "20px 0 0", "fontSize": "11px", "fontWeight": "800", "letterSpacing": "0.14em", "textTransform": "uppercase", "color": "#1A56DB" }}>
                {t('equipe.t03')}
              </div>
              <p style={{ "margin": "10px 0 0", "textAlign": "justify", "hyphens": "auto", "fontFamily": "'Inter Tight', -apple-system, sans-serif", "fontSize": "clamp(17px, 1.9vw, 21px)", "fontWeight": "500", "letterSpacing": "-0.015em", "lineHeight": "1.55", "color": "#2A2A35" }}>
                {t('equipe.t04')}
              </p>
              {"\n"}
              <div className="hv2-founder-links" style={{ "marginTop": "24px", "display": "flex", "gap": "14px", "flexWrap": "wrap" }}>
                {"\n"}
                <a href="#" onClick={inert} style={{ "display": "inline-flex", "alignItems": "center", "gap": "7px", "padding": "10px 18px", "background": "#ffffff", "border": "1px solid #ECECEA", "borderRadius": "12px", "fontSize": "14px", "fontWeight": "600", "letterSpacing": "-0.01em", "color": "#0a0a0f" }}>
                  {t('equipe.t05')}
                </a>
                {"\n"}
                <a href="#" onClick={inert} style={{ "display": "inline-flex", "alignItems": "center", "gap": "8px", "padding": "10px 18px", "background": "#ffffff", "border": "1px solid #ECECEA", "borderRadius": "12px", "fontSize": "14px", "fontWeight": "600", "letterSpacing": "-0.01em", "color": "#0a0a0f" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" fill="none" stroke="#1a56db" strokeWidth="1.8" />
                    <polygon points="10 8.5 16 12 10 15.5" fill="#1a56db" stroke="none" />
                  </svg>
                  {t('equipe.t06')}
                  <span style={{ "fontWeight": "500", "color": "#9CA3AF" }}>
                    {t('equipe.t07')}
                  </span>
                </a>
                {"\n"}
              </div>
              {"\n"}
            </div>
            {"\n"}
          </div>
          {"\n"}
        </section>
        {/* ═══ testi ═══ */}
        {testimonialsOn && (
          <section style={{ "background": "rgb(255, 255, 255)", "borderTop": "1px solid rgb(232, 232, 228)", "padding": "44px 40px 48px" }}>
            {"\n"}
            <div style={{ "maxWidth": "1000px", "margin": "0px auto" }}>
              {"\n"}
              <div style={{ "display": "flex", "alignItems": "center", "gap": "14px", "marginBottom": "14px" }}>
                {"\n"}
                <div className="hv2-eyebrow">
                  {t('testi.t01')}
                </div>
                {"\n"}
                <div style={{ "flex": "1 1 0%" }} />
                {"\n"}
                <button type="button" onClick={(ev) => scrollTesti(-1, ev)} aria-label={t('testi.t02')} className="hv2-testi-nav" style={{ "width": "32px", "height": "32px", "borderRadius": "50%", "background": "#ffffff", "border": "1px solid #ECECEA", "color": "#6B7280", "display": "flex", "alignItems": "center", "justifyContent": "center", "cursor": "pointer", "font": "inherit", "transition": "all .15s ease" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                {"\n"}
                <button type="button" onClick={(ev) => scrollTesti(1, ev)} aria-label={t('testi.t03')} className="hv2-testi-nav" style={{ "width": "32px", "height": "32px", "borderRadius": "50%", "background": "#ffffff", "border": "1px solid #ECECEA", "color": "#6B7280", "display": "flex", "alignItems": "center", "justifyContent": "center", "cursor": "pointer", "font": "inherit", "transition": "all .15s ease" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
                {"\n"}
              </div>
              {"\n"}
              <div className="hv2-testi-rail" style={{ "display": "flex", "gap": "12px", "overflowX": "auto", "scrollSnapType": "x mandatory", "padding": "2px 0 6px" }}>
                {"\n"}
                <a className="hv2-testi" style={{ "position": "relative", "flex": "0 0 288px", "scrollSnapAlign": "start", "aspectRatio": "4 / 3", "borderRadius": "14px", "overflow": "hidden", "background": "#E9E9E4", "color": "inherit", "textDecoration": "none", "transition": "all .18s ease" }}>
                  {"\n"}
                  <HV3Slot id="ph-temoin-1" data-portrait="" src="/home-v3/temoin-1.png" hostStyle={{ "display": "block", "position": "absolute", "inset": "0", "width": "100%", "height": "100%" }} frameStyle={{}} imgStyle={IMG_PORTRAIT_X} />
                  {"\n"}
                  <div style={{ "position": "absolute", "inset": "auto 0 0", "padding": "36px 14px 12px", "background": "linear-gradient(to top, rgba(10,10,15,0.9) 0%, rgba(10,10,15,0.5) 48%, rgba(10,10,15,0) 100%)", "pointerEvents": "none" }}>
                    {"\n"}
                    <div style={{ "paddingRight": "40px" }}>
                      {"\n"}
                      <div style={{ "fontSize": "9px", "fontWeight": "800", "letterSpacing": "0.11em", "textTransform": "uppercase", "color": "rgba(255,255,255,0.6)" }}>
                        {t('testi.t05')}
                      </div>
                      {"\n"}
                      <div style={{ "marginTop": "5px", "fontSize": "12.5px", "fontWeight": "700", "letterSpacing": "-0.015em", "lineHeight": "1.28", "color": "rgba(255,255,255,0.9)" }}>
                        {t('testi.t06')}
                      </div>
                      {"\n"}
                      <div style={{ "marginTop": "5px", "fontSize": "11px", "fontWeight": "500", "color": "rgba(255,255,255,0.76)" }}>
                        {t('testi.t07')}
                      </div>
                      {"\n"}
                    </div>
                    {"\n"}
                  </div>
                  {"\n"}
                  <span aria-hidden="true" className="hv2-testi-arrow" style={{ "position": "absolute", "right": "11px", "bottom": "11px", "width": "28px", "height": "28px", "borderRadius": "50%", "background": "#1A56DB", "display": "flex", "alignItems": "center", "justifyContent": "center", "boxShadow": "0 4px 14px rgba(10,10,15,0.26)", "transition": "transform .18s ease, background .18s ease" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </span>
                  {"\n"}
                </a>
                {"\n"}
                <a className="hv2-testi" style={{ "position": "relative", "flex": "0 0 288px", "scrollSnapAlign": "start", "aspectRatio": "4 / 3", "borderRadius": "14px", "overflow": "hidden", "background": "#E9E9E4", "color": "inherit", "textDecoration": "none", "transition": "all .18s ease" }}>
                  {"\n"}
                  <HV3Slot id="ph-temoin-2" data-portrait="" src="/home-v3/temoin-2.png" hostStyle={{ "display": "block", "position": "absolute", "inset": "0", "width": "100%", "height": "100%" }} frameStyle={{}} imgStyle={IMG_PORTRAIT_X} />
                  {"\n"}
                  <div style={{ "position": "absolute", "inset": "auto 0 0", "padding": "36px 14px 12px", "background": "linear-gradient(to top, rgba(10,10,15,0.9) 0%, rgba(10,10,15,0.5) 48%, rgba(10,10,15,0) 100%)", "pointerEvents": "none" }}>
                    {"\n"}
                    <div style={{ "paddingRight": "40px" }}>
                      {"\n"}
                      <div style={{ "fontSize": "9px", "fontWeight": "800", "letterSpacing": "0.11em", "textTransform": "uppercase", "color": "rgba(255,255,255,0.6)" }}>
                        {t('testi.t09')}
                      </div>
                      {"\n"}
                      <div style={{ "marginTop": "5px", "fontSize": "12.5px", "fontWeight": "700", "letterSpacing": "-0.015em", "lineHeight": "1.28", "color": "rgba(255,255,255,0.9)" }}>
                        {t('testi.t10')}
                      </div>
                      {"\n"}
                      <div style={{ "marginTop": "5px", "fontSize": "11px", "fontWeight": "500", "color": "rgba(255,255,255,0.76)" }}>
                        {t('testi.t11')}
                      </div>
                      {"\n"}
                    </div>
                    {"\n"}
                  </div>
                  {"\n"}
                  <span aria-hidden="true" className="hv2-testi-arrow" style={{ "position": "absolute", "right": "11px", "bottom": "11px", "width": "28px", "height": "28px", "borderRadius": "50%", "background": "#1A56DB", "display": "flex", "alignItems": "center", "justifyContent": "center", "boxShadow": "0 4px 14px rgba(10,10,15,0.26)", "transition": "transform .18s ease, background .18s ease" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </span>
                  {"\n"}
                </a>
                {"\n"}
                <a className="hv2-testi" style={{ "position": "relative", "flex": "0 0 288px", "scrollSnapAlign": "start", "aspectRatio": "4 / 3", "borderRadius": "14px", "overflow": "hidden", "background": "#E9E9E4", "color": "inherit", "textDecoration": "none", "transition": "all .18s ease" }}>
                  {"\n"}
                  <HV3Slot id="ph-temoin-3" data-portrait="" src="/home-v3/temoin-3.png" hostStyle={{ "display": "block", "position": "absolute", "inset": "0", "width": "100%", "height": "100%" }} frameStyle={{}} imgStyle={IMG_PORTRAIT_X} />
                  {"\n"}
                  <div style={{ "position": "absolute", "inset": "auto 0 0", "padding": "36px 14px 12px", "background": "linear-gradient(to top, rgba(10,10,15,0.9) 0%, rgba(10,10,15,0.5) 48%, rgba(10,10,15,0) 100%)", "pointerEvents": "none" }}>
                    {"\n"}
                    <div style={{ "paddingRight": "40px" }}>
                      {"\n"}
                      <div style={{ "fontSize": "9px", "fontWeight": "800", "letterSpacing": "0.11em", "textTransform": "uppercase", "color": "rgba(255,255,255,0.6)" }}>
                        {t('testi.t13')}
                      </div>
                      {"\n"}
                      <div style={{ "marginTop": "5px", "fontSize": "12.5px", "fontWeight": "700", "letterSpacing": "-0.015em", "lineHeight": "1.28", "color": "rgba(255,255,255,0.9)" }}>
                        {t('testi.t14')}
                      </div>
                      {"\n"}
                      <div style={{ "marginTop": "5px", "fontSize": "11px", "fontWeight": "500", "color": "rgba(255,255,255,0.76)" }}>
                        {t('testi.t15')}
                      </div>
                      {"\n"}
                    </div>
                    {"\n"}
                  </div>
                  {"\n"}
                  <span aria-hidden="true" className="hv2-testi-arrow" style={{ "position": "absolute", "right": "11px", "bottom": "11px", "width": "28px", "height": "28px", "borderRadius": "50%", "background": "#1A56DB", "display": "flex", "alignItems": "center", "justifyContent": "center", "boxShadow": "0 4px 14px rgba(10,10,15,0.26)", "transition": "transform .18s ease, background .18s ease" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </span>
                  {"\n"}
                </a>
                {"\n"}
                <a className="hv2-testi" style={{ "position": "relative", "flex": "0 0 288px", "scrollSnapAlign": "start", "aspectRatio": "4 / 3", "borderRadius": "14px", "overflow": "hidden", "background": "#E9E9E4", "color": "inherit", "textDecoration": "none", "transition": "all .18s ease" }}>
                  {"\n"}
                  <HV3Slot id="ph-temoin-4" data-portrait="" src="/home-v3/temoin-4.png" hostStyle={{ "display": "block", "position": "absolute", "inset": "0", "width": "100%", "height": "100%" }} frameStyle={{}} imgStyle={IMG_PORTRAIT_4} />
                  {"\n"}
                  <div style={{ "position": "absolute", "inset": "auto 0 0", "padding": "36px 14px 12px", "background": "linear-gradient(to top, rgba(10,10,15,0.9) 0%, rgba(10,10,15,0.5) 48%, rgba(10,10,15,0) 100%)", "pointerEvents": "none" }}>
                    {"\n"}
                    <div style={{ "paddingRight": "40px" }}>
                      {"\n"}
                      <div style={{ "fontSize": "9px", "fontWeight": "800", "letterSpacing": "0.11em", "textTransform": "uppercase", "color": "rgba(255,255,255,0.6)" }}>
                        {t('testi.t17')}
                      </div>
                      {"\n"}
                      <div style={{ "marginTop": "5px", "fontSize": "12.5px", "fontWeight": "700", "letterSpacing": "-0.015em", "lineHeight": "1.28", "color": "rgba(255,255,255,0.9)" }}>
                        {t('testi.t18')}
                      </div>
                      {"\n"}
                      <div style={{ "marginTop": "5px", "fontSize": "11px", "fontWeight": "500", "color": "rgba(255,255,255,0.76)" }}>
                        {t('testi.t19')}
                      </div>
                      {"\n"}
                    </div>
                    {"\n"}
                  </div>
                  {"\n"}
                  <span aria-hidden="true" className="hv2-testi-arrow" style={{ "position": "absolute", "right": "11px", "bottom": "11px", "width": "28px", "height": "28px", "borderRadius": "50%", "background": "#1A56DB", "display": "flex", "alignItems": "center", "justifyContent": "center", "boxShadow": "0 4px 14px rgba(10,10,15,0.26)", "transition": "transform .18s ease, background .18s ease" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </span>
                  {"\n"}
                </a>
                {"\n"}
              </div>
              {"\n"}
            </div>
            {"\n"}
          </section>
        )}
        {/* ═══ cta ═══ */}
        <section style={{ "background": "rgb(244, 244, 241)", "borderTop": "1px solid rgb(232, 232, 228)", "padding": "88px 40px 48px", "textAlign": "center", "position": "relative", "overflow": "hidden" }}>
          <div style={{ "maxWidth": "640px", "margin": "0px auto", "position": "relative" }}>
            <h2 style={{ "fontSize": "clamp(28px, 3.6vw, 40px)", "fontWeight": "600", "letterSpacing": "-0.032em", "lineHeight": "1.15", "color": "rgb(10, 10, 15)", "margin": "0px" }}>
              {t('cta.t01')}
              <br />
              <span lang="fr" style={{ "color": "rgb(26, 86, 219)" }}>
                {t('cta.t02')}
              </span>
            </h2>
            <p style={{ "fontSize": "16px", "fontWeight": "400", "lineHeight": "1.6", "color": "rgb(75, 85, 99)", "margin": "16px auto 28px", "maxWidth": "460px" }}>
              {t('cta.t03')}
            </p>
            <button onClick={goStart} type="button" className="hv2-btn-primary-lift" style={{ "display": "inline-flex", "alignItems": "center", "gap": "8px", "padding": "12px 22px", "borderWidth": "medium", "borderStyle": "none", "borderColor": "currentcolor", "borderImage": "none", "background": "rgb(26, 86, 219)", "color": "rgb(255, 255, 255)", "borderRadius": "10px", "fontSize": "15px", "fontWeight": "500", "letterSpacing": "-0.01em", "fontFamily": "inherit", "cursor": "pointer", "transition": "0.15s" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              {t('cta.t04')}
            </button>
          </div>
        </section>

      <SiteFooterV2 />

      <VideoModal
        open={videoOpen}
        video={heroVideo}
        onClose={() => setVideoOpen(false)}
        label={t('video.label')}
        closeLabel={t('video.close')}
        reducedMotion={prefersReducedMotion()}
        testId="hero-video-modal"
        onUnavailable={onVideoUnavailable}
      />
    </div>
  );
}
